import { ISubMenu } from "@/src/data/menus";
import React, { useEffect, useState, useRef, useCallback } from "react";
import { SmartLink } from "@/src/components/v2/SmartLink/SmartLink";
import ChevronDown from "@/src/media/icons/chevron-down-solid.svg";
import SVG from "react-inlinesvg";
import { BrandService } from "@/src/BrandService";
import clsx from "clsx";
import DogIcon from "@/src/media/yellow-dog.svg";
import CatIcon from "@/src/media/blue-cat.svg";
import CoverageIcon from "@/src/media/coverage-icon.svg";
import CompareIcon from "@/src/media/compare-icon.svg";
import EmployerIcon from "@/src/media/employers-icon.svg";

const headerMenus = BrandService.getCurrentBrandContent().menus.siteHeader;

interface HeaderMenuProps {
    breakpoint: string;
    isMobileMenuOpen: boolean;
    onNavigate?: () => void;
    isdefaultBrand: boolean;
}

const HeaderMenu = (props: HeaderMenuProps) => {
    const { onNavigate, breakpoint, isMobileMenuOpen, isdefaultBrand } = props;
    const [currentMenuIndex, setCurrentMenuIndex] = useState<number>(-1);
    // Nested lists (Dog Breeds / Cat Breeds) currently expanded on desktop, keyed by parent url
    const [openNestedUrls, setOpenNestedUrls] = useState<string[]>([]);
    const prevBreakpointRef = useRef<string | null>(null);
    const isMobileMenu = ["base", "sm", "md"].includes(breakpoint);
    const isDesktopMenu = ["lg", "xl", "2xl"].includes(breakpoint);

    const closeSubmenu = useCallback(() => {
        setCurrentMenuIndex(-1);
        setOpenNestedUrls([]);
    }, []);

    // Disable body scroll when submenu is open on mobile
    useEffect(() => {
        const overflowClass = "overflow-hidden";

        if (currentMenuIndex > -1 && isMobileMenu) {
            document.body.classList.add(overflowClass);
        } else {
            document.body.classList.remove(overflowClass);
        }

        return () => {
            document.body.classList.remove(overflowClass);
        };
    }, [currentMenuIndex, breakpoint, isMobileMenu]);

    // Close menu when switching between md/lg breakpoints
    useEffect(() => {
        const prev = prevBreakpointRef.current;
        const isPrevMobile = !prev || ["base", "sm", "md"].includes(prev);

        // If the previous breakpoint was mobile and the current is not, close the menu
        if (isPrevMobile !== isMobileMenu) {
            // Closes submenu
            closeSubmenu();
            // Close the mobile toggle menu
            onNavigate?.();
        }

        prevBreakpointRef.current = breakpoint;
    }, [breakpoint, isMobileMenu, onNavigate, closeSubmenu]);

    // Close any submenu when mobile menu is closed
    useEffect(() => {
        if (!isMobileMenuOpen && isMobileMenu) {
            closeSubmenu();
        }
    }, [isMobileMenuOpen, breakpoint, isMobileMenu, closeSubmenu]);

    const handleNavigate = () => {
        closeSubmenu();
        if (onNavigate) {
            onNavigate();
        }
    };

    const handleToggleSubmenuIndex = useCallback(
        (menuIndex: number) => {
            if (menuIndex === currentMenuIndex) {
                closeSubmenu();
            } else {
                setCurrentMenuIndex(menuIndex);
                setOpenNestedUrls([]);
            }
        },
        [currentMenuIndex, closeSubmenu]
    );

    // Needed for combined keyboard and mouse navigation so that menu doesn't stick open when using keyboard
    // and then moving the mouse over the menu
    const handleMouseLeave = useCallback(() => {
        if (isDesktopMenu) {
            closeSubmenu();
        }
    }, [isDesktopMenu, closeSubmenu]);

    // Closes submenu when focus is lost
    // Only applies to Anchor links on desktop, since they don't have a submenu
    const handleFocus = useCallback(() => {
        if (isDesktopMenu) {
            closeSubmenu();
        }
    }, [isDesktopMenu, closeSubmenu]);

    // Enables keyboard navigation for submenu items
    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent, index: number) => {
            if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                handleToggleSubmenuIndex(index);
            }
            if (event.key === "Escape") {
                event.preventDefault();
                closeSubmenu();
            }
        },
        [handleToggleSubmenuIndex, closeSubmenu]
    );

    const handleSubmenuEscape = useCallback(
        (event: React.KeyboardEvent) => {
            if (event.key === "Escape") {
                event.preventDefault();
                closeSubmenu();
            }
        },
        [closeSubmenu]
    );

    function renderSubmenuItem(item: ISubMenu, hasHeader = false, isNested = false) {
        const conditionalLinkClasses = {
            "text-body2 ml-6 lg:ml-2": hasHeader && !item.isHeader,
            "text-body2 ml-6 lg:ml-4": isNested
        };

        const isDog = isdefaultBrand && item.title === "Dog Insurance";
        const isCat = isdefaultBrand && item.title === "Cat Insurance";
        const isCoverage = isdefaultBrand && item.title === "What Does Pet Insurance Cover?";
        const isCompare = isdefaultBrand && item.title === "Compare Pet Insurance Plans";
        const isEmployers = isdefaultBrand && item.title === "HR Professionals & Brokers";

        if (item.isHeader) {
            return <span className="cursor-text select-text p-2 text-body2 font-semibold uppercase text-text-primary hover:bg-transparent lg:pl-0">{item.title}</span>;
        }

        /// dog and cat items
        if (isDog || isCat) {
            const dogItem = {
                icon: DogIcon.src,
                title: "Dog Insurance",
                href: "/dog-insurance",
                price: (
                    <>
                        Starting from $15/mo<sup>^</sup>
                    </>
                )
            };

            const catItem = {
                icon: CatIcon.src,
                title: "Cat Insurance",
                href: "/cat-insurance",
                price: (
                    <>
                        Starting from $9/mo<sup>^^</sup>
                    </>
                )
            };

            if (item.title === "Cat Insurance") {
                return null;
            }

            return (
                <div data-testid="dog-cat-cards" className="mb-1 mt-4 flex w-full flex-col gap-5 sm:flex-row lg:mb-4 lg:mt-4 lg:flex-col">
                    {[dogItem, catItem].map(({ icon, title, href, price }) => (
                        <SmartLink
                            href={href}
                            key={title}
                            tabIndex={0}
                            className="border-accent-secondary-weak relative w-full flex-1 rounded-xl border-2 border-border-dark bg-background-surface hover:border-background-secondary hover:bg-accent-secondary-background"
                            onClick={handleNavigate}
                            onKeyDown={handleSubmenuEscape}
                        >
                            <div className="flex items-center py-5 pl-5">
                                <div className="absolute left-2 -mt-1">
                                    <SVG src={icon} className="h-[93px] w-24" />
                                </div>
                                <div className="ml-[92px] flex flex-col text-left">
                                    <span className="text-base font-bold text-text-primary">{title}</span>
                                    <span className="text-body2 text-text-gray">{price}</span>
                                </div>
                            </div>
                        </SmartLink>
                    ))}
                </div>
            );
        }
        ///Coverage and Compare items
        if (isCoverage || isCompare) {
            const coverageItem = {
                icon: CoverageIcon.src,
                title: "What Does Pet Insurance Cover?",
                href: "/what-does-pet-insurance-cover",
                price: null
            };

            const compareItem = {
                icon: CompareIcon.src,
                title: "Compare Pet Insurance Plans",
                href: "/compare-pet-insurance",
                price: null
            };

            if (item.title === "Compare Pet Insurance Plans") {
                return null;
            }

            return (
                <div data-testid="coverage-compare-cards" className="mb-1 mt-4 flex w-full flex-col gap-5 sm:flex-row lg:mb-6 lg:mt-4 lg:flex-col">
                    {[coverageItem, compareItem].map(({ icon, title, href }) => (
                        <SmartLink
                            href={href}
                            key={title}
                            tabIndex={0}
                            className="border-accent-secondary-weak relative w-full flex-1 rounded-xl border-2 border-border-dark bg-background-surface hover:border-background-secondary hover:bg-accent-secondary-background"
                            onClick={handleNavigate}
                            onKeyDown={handleSubmenuEscape}
                        >
                            <div className="flex items-center py-4 pl-5 md:py-4">
                                <div className="absolute left-2">
                                    <SVG src={icon} className="h-auto w-24" />
                                </div>
                                <div className="ml-[92px] flex flex-col text-left">
                                    <span className="mr-6 py-0 text-base font-bold text-text-primary md:mr-10 md:py-3 lg:mr-3 lg:py-0">{title}</span>
                                </div>
                            </div>
                        </SmartLink>
                    ))}
                </div>
            );
        }

        ///Employer Benefits items
        if (isEmployers) {
            const employerItem = {
                icon: EmployerIcon.src,
                title: "HR Professionals & Brokers",
                href: "/employers",
                price: <>20% employee discount°</>
            };

            return (
                <div data-testid="Employers-card" className="mb-1 mt-4 flex w-full">
                    {[employerItem].map(({ icon, title, href, price }) => (
                        <SmartLink
                            href={href}
                            key={title}
                            tabIndex={0}
                            className="border-accent-secondary-weak relative w-full flex-1 rounded-xl border-2 border-border-dark bg-background-surface py-0 hover:border-background-secondary hover:bg-accent-secondary-background sm:py-3 lg:w-[340px] lg:py-0"
                            onClick={handleNavigate}
                            onKeyDown={handleSubmenuEscape}
                        >
                            <div className="flex items-center py-2 pl-5">
                                <div className="absolute left-3 -mt-2 lg:-mt-1">
                                    <SVG src={icon} className="h-[95px] w-24" />
                                </div>
                                <div className="ml-[100px] flex flex-col text-left">
                                    <span className="mr-16 text-base font-bold text-text-primary">{title}</span>
                                    <span className="text-body2 text-text-gray">{price}</span>
                                </div>
                            </div>
                        </SmartLink>
                    ))}
                </div>
            );
        }

        const linkTextClasses = clsx("text-body1 font-medium", isdefaultBrand ? "text-text-primary" : "text-text-secondary");
        const linkPaddingClasses = clsx("p-2", isdefaultBrand && "py-3 lg:py-1");
        const linkHoverClasses = isdefaultBrand ? "hover:bg-accent-secondary-background" : "hover:bg-background-medium";
        const linkClasses = clsx("w-full rounded-lg", linkPaddingClasses, linkTextClasses, linkHoverClasses, conditionalLinkClasses);

        // Items with their own submenu (e.g. Dog Breeds / Cat Breeds) expand downward on desktop hover/focus and
        // stay open until the chevron is clicked or the dropdown closes, so nothing shifts under the cursor.
        // On mobile the nested links are always shown, indented under the parent link.
        if (item.submenu?.length) {
            const isNestedOpen = openNestedUrls.includes(item.url);
            const nestedId = `nested-submenu-${item.url.replace(/\W+/g, "-")}`;
            const openNested = () => {
                if (isDesktopMenu) {
                    setOpenNestedUrls(urls => (urls.includes(item.url) ? urls : [...urls, item.url]));
                }
            };
            const toggleNested = () => {
                setOpenNestedUrls(urls => (urls.includes(item.url) ? urls.filter(url => url !== item.url) : [...urls, item.url]));
            };

            return (
                <div className="flex w-full flex-col" onMouseEnter={openNested}>
                    {/* The label link navigates; the chevron button fills the rest of the row and toggles the list */}
                    <div className={clsx("flex w-full items-stretch rounded-lg", linkTextClasses, linkHoverClasses, conditionalLinkClasses)}>
                        <SmartLink
                            tabIndex={0}
                            href={item.url}
                            className={clsx(linkPaddingClasses, "max-lg:w-full lg:block")}
                            onClick={handleNavigate}
                            onKeyDown={handleSubmenuEscape}
                            onFocus={openNested}
                        >
                            {item.title}
                        </SmartLink>
                        <button
                            type="button"
                            tabIndex={-1}
                            aria-label={`${isNestedOpen ? "Collapse" : "Expand"} ${item.title}`}
                            aria-expanded={isNestedOpen}
                            aria-controls={nestedId}
                            onClick={toggleNested}
                            onKeyDown={handleSubmenuEscape}
                            className="flex flex-1 items-center pr-2 max-lg:hidden"
                        >
                            <SVG src={ChevronDown.src} width={12} height={12} className={clsx("transition-transform ease-linear", isNestedOpen && "rotate-180")} />
                        </button>
                    </div>
                    <ul id={nestedId} role="group" className={clsx("flex-col gap-1 max-lg:flex lg:mt-1", isNestedOpen ? "lg:flex" : "lg:hidden")}>
                        {item.submenu.map((nestedItem, index) => (
                            <li role="none" className="flex" key={nestedItem.url + index.toString()}>
                                {renderSubmenuItem(nestedItem, false, true)}
                            </li>
                        ))}
                    </ul>
                </div>
            );
        }

        return (
            <SmartLink tabIndex={0} href={item.url} key={item.url} className={clsx(linkClasses, "lg:block")} onClick={handleNavigate} onKeyDown={handleSubmenuEscape}>
                {item.title}
            </SmartLink>
        );
    }

    function renderMenuItem(menuIndex: number, title: string, href?: string, submenus?: ISubMenu[] | ISubMenu[][]) {
        const hasSubmenu = !!submenus;
        const isOpen = menuIndex === currentMenuIndex;

        const itemClass = clsx(
            "flex cursor-pointer flex-row items-center gap-2 rounded-lg p-4 text-button-md font-semibold text-accent-secondary-ada hover:bg-background-medium max-lg:text-subheadline1 lg:p-3",
            isdefaultBrand && "p-5 "
        );
        let menuContent = href ? (
            <>{title}</>
        ) : (
            <button
                role="menuitem"
                tabIndex={0}
                onClick={() => handleToggleSubmenuIndex(menuIndex)}
                className={clsx("text-left max-lg:w-full", itemClass)}
                onKeyDown={event => handleKeyDown(event, menuIndex)}
                aria-haspopup={hasSubmenu ? "true" : undefined}
                aria-expanded={hasSubmenu ? isOpen : undefined}
                aria-controls={hasSubmenu ? `submenu-${menuIndex}` : undefined}
            >
                <div className="flex-1">{title}</div>
                {submenus && (
                    <SVG
                        src={ChevronDown.src}
                        width={isMobileMenu ? 16 : 12}
                        height={isMobileMenu ? 16 : 12}
                        className={`transition-transform ease-linear ${isOpen ? "max-lg:rotate-180" : ""}`}
                    />
                )}
            </button>
        );

        const renderSubmenuContent = () => {
            if (!hasSubmenu) return;
            const hasNestedMenu = Array.isArray(submenus[0]);
            const hasExpandableItems = !hasNestedMenu && (submenus as ISubMenu[]).some(item => !!item.submenu?.length);

            const submenuContent = () => {
                // If the submenu has submenus (is a 2d array), render the submenus in a nested list
                if (hasNestedMenu) {
                    return (submenus as ISubMenu[][]).map((submenu, index) => {
                        const hasHeader = submenu.some(item => item.isHeader);

                        if (isdefaultBrand) {
                            const cardTitles = ["Dog Insurance", "Cat Insurance", "What Does Pet Insurance Cover?", "Compare Pet Insurance Plans", "HR Professionals & Brokers"];

                            // Split into two groups: dog/cat cards and text items
                            const cardItems = submenu.filter(item => cardTitles.includes(item.title));
                            const textItems = submenu.filter(item => !cardTitles.includes(item.title));

                            return (
                                <li key={`submenu_root_li_${index}`} className="lg:flex lg:gap-4">
                                    {/* Cards column */}
                                    {cardItems.length > 0 && (
                                        <ul className="lg:w-[300px] lg:flex-shrink-0">
                                            {cardItems.map((item: ISubMenu, index: number) => (
                                                <li key={item.url + index.toString()} className="flex">
                                                    {renderSubmenuItem(item, hasHeader)}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                    {/* Text items column */}
                                    <ul className={`${hasHeader ? "gap-1" : "gap-2"} ${isdefaultBrand ? "mt-4" : " "} lg:flex lg:flex-col`}>
                                        {textItems.map((item: ISubMenu, index: number) => (
                                            <li key={item.url + index.toString()} className="flex">
                                                {renderSubmenuItem(item, hasHeader)}
                                            </li>
                                        ))}
                                    </ul>
                                </li>
                            );
                        }

                        return (
                            <li key={`submenu_root_li_${index}`}>
                                <ul className={`${hasHeader ? "gap-1" : "gap-2"}`}>
                                    {submenu.map((item: ISubMenu, index: number) => {
                                        return (
                                            <li key={item.url + index.toString()} className="flex">
                                                {renderSubmenuItem(item, hasHeader)}
                                            </li>
                                        );
                                    })}
                                </ul>
                            </li>
                        );
                    });
                }

                // Render submenu in a single list
                return (submenus as ISubMenu[]).map((item, index) => (
                    <li className="flex" key={item.url + index.toString()}>
                        {renderSubmenuItem(item)}
                    </li>
                ));
            };

            // Mobile: show on click
            const mobileToggleClickClasses = {
                "max-lg:block": isOpen,
                "max-lg:hidden": !isOpen
            };

            let hasCardItems = false;

            if (hasNestedMenu && isdefaultBrand) {
                const cardTitles = ["Dog Insurance", "Cat Insurance", "What Does Pet Insurance Cover?", "Compare Pet Insurance Plans", "HR Professionals & Brokers"];

                hasCardItems = (submenus as ISubMenu[][]).some(group => group.some(item => cardTitles.includes(item.title)));
            }

            // Displays submenus on (desktop) hover
            const desktopClasses = clsx(
                `${
                    hasCardItems ? "lg:w-[582px] lg:max-w-[582px] lg:py-1 lg:rounded-2xl lg:px-4" : "lg:py-6 lg:rounded-lg lg:min-w-max lg:px-10"
                } lg:absolute lg:left-0 lg:top-full lg:z-50 lg:bg-background-surface lg:drop-shadow-lg`,
                {
                    // With several nested lists open the panel can outgrow short viewports; the header is sticky so page scroll can't reveal it
                    "lg:max-h-[calc(100vh-8rem)] lg:overflow-y-auto": hasExpandableItems,
                    "lg:flex": isOpen,
                    "lg:hidden lg:group-hover:flex": !isOpen
                }
            );

            const submenuConditionalClasses = {
                "lg:flex-row": hasNestedMenu,
                "lg:flex-col lg:gap-2": !hasNestedMenu
            };
            const submenuId = `submenu-${menuIndex}`;
            const submenu = (
                <ul
                    role="menu"
                    id={submenuId}
                    data-testid={submenuId}
                    className={clsx(
                        `${
                            isdefaultBrand
                                ? "bg-background-light-gray px-5 pb-3 max-lg:border-y-2 max-lg:border-background-medium lg:px-4"
                                : "max-lg:ml-4 max-lg:border-l max-lg:px-2"
                        } max-lg:border-accent-background`,
                        mobileToggleClickClasses,
                        desktopClasses,
                        submenuConditionalClasses
                    )}
                >
                    {submenuContent()}
                </ul>
            );

            return submenu;
        };

        if (href) {
            menuContent = (
                <SmartLink href={href} className={itemClass} onClick={handleNavigate} role="menuitem" onFocus={handleFocus} tabIndex={0}>
                    {menuContent}
                </SmartLink>
            );
        }

        return (
            <li className={clsx("group relative")} role="none" key={`root_li_${menuIndex}`} onMouseLeave={handleMouseLeave}>
                {menuContent}
                {renderSubmenuContent()}
            </li>
        );
    }

    const menuWrapperConditionalClasses = {
        "max-lg:overflow-y-scroll": isMobileMenu && isMobileMenuOpen && currentMenuIndex > -1
    };

    return (
        <ul data-testid="header-nav-menu" className={clsx("max-lg:max-h-[90vh] max-lg:p-[1px] lg:flex lg:w-max lg:flex-row", menuWrapperConditionalClasses)} role="menubar">
            {headerMenus.map((menu, index) => renderMenuItem(index, menu.title, menu.href, menu?.submenu))}
        </ul>
    );
};

export default HeaderMenu;
